/**
 * React Query hooks for notification delivery statistics
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  getDeliveryStats,
  getRecentFailures,
  getNotificationStats,
  retryFailedDelivery,
  type DeliveryStats,
  type RecentFailure,
  type NotificationStats,
} from '@/services/notifications';

export const deliveryStatsKeys = {
  all: ['delivery-stats'] as const,
  stats: (companyId: string) => [...deliveryStatsKeys.all, 'stats', companyId] as const,
  failures: (companyId: string) => [...deliveryStatsKeys.all, 'failures', companyId] as const,
  notificationStats: (companyId: string) => [...deliveryStatsKeys.all, 'notification-stats', companyId] as const,
};

export function useDeliveryStats(companyId: string | undefined) {
  return useQuery({
    queryKey: deliveryStatsKeys.stats(companyId || ''),
    queryFn: () => getDeliveryStats(companyId!),
    enabled: !!companyId,
    refetchInterval: 10 * 60 * 1000, // 10 minutes - admin dashboard, manual refresh available
    refetchOnWindowFocus: true,
  });
}

export function useRecentFailures(companyId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: deliveryStatsKeys.failures(companyId || ''),
    queryFn: () => getRecentFailures(companyId!, limit),
    enabled: !!companyId,
  });
}

export function useNotificationStats(companyId: string | undefined) {
  return useQuery({
    queryKey: deliveryStatsKeys.notificationStats(companyId || ''),
    queryFn: () => getNotificationStats(companyId!),
    enabled: !!companyId,
  });
}

export function useRetryFailedDelivery(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: retryFailedDelivery,
    onSuccess: (success) => {
      if (success && companyId) {
        queryClient.invalidateQueries({ queryKey: deliveryStatsKeys.stats(companyId) });
        queryClient.invalidateQueries({ queryKey: deliveryStatsKeys.failures(companyId) });
        toast({
          title: 'Retry scheduled',
          description: 'The delivery will be retried shortly.',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Retry failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
